import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

type IssueStatus = "open" | "investigating" | "resolved" | "ignored";
type IssueSeverity = "low" | "medium" | "high";

const statusColors: Record<IssueStatus, string> = {
  open: "var(--danger)",
  investigating: "var(--warning)",
  resolved: "var(--success)",
  ignored: "var(--ink-mute)",
};

const severityColors: Record<IssueSeverity, string> = {
  low: "var(--ink-mute)",
  medium: "var(--warning)",
  high: "var(--danger)",
};

const NEXT_STATUSES: Record<IssueStatus, IssueStatus[]> = {
  open: ["investigating", "resolved", "ignored"],
  investigating: ["resolved", "ignored"],
  resolved: [],
  ignored: ["open"],
};

const TYPE_LABELS: Record<string, string> = {
  reacquired_without_sale: "Back in stock, never sold",
  invoice_without_purchase: "Invoiced, never purchased",
  foreign_owner: "Passport owned elsewhere",
  mint_unconfirmed: "Mint not confirmed",
  document_without_serials: "Document has no serials",
};

const TYPE_FILTERS = ["All types", ...Object.keys(TYPE_LABELS)] as const;
const STATUS_FILTERS = [
  "All status",
  "open",
  "investigating",
  "resolved",
  "ignored",
] as const;
const LIMIT = 20;

interface IssueItem {
  id: string;
  type: string;
  status: IssueStatus;
  severity: IssueSeverity;
  dppId: string | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  sourceType: string | null;
  sourceId: string;
  sourceNumber: string | null;
  title: string;
  detail: Record<string, unknown> | null;
  statusNote: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

interface IssueList {
  items: IssueItem[];
  total: number;
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: 9.5,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        padding: "3px 8px",
        border: `1px solid ${color}`,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <span
        className="mono-cap text-mute"
        style={{ fontSize: 10, letterSpacing: "0.12em" }}
      >
        {label}
      </span>
      <span style={{ fontSize: 13, color: "var(--ink)" }}>{value || "—"}</span>
    </div>
  );
}

export function IssuesPage() {
  const { can } = useAuth();
  const canManage = can("issue:update");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("open");
  const [typeFilter, setTypeFilter] = useState<string>("All types");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<IssueList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<IssueItem | null>(null);
  const [nextStatus, setNextStatus] = useState<IssueStatus | "">("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, typeFilter]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      page: String(page),
      limit: String(LIMIT),
    });
    if (statusFilter !== "All status") params.set("status", statusFilter);
    if (typeFilter !== "All types") params.set("type", typeFilter);
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

    api
      .get<IssueList>(`/admin/issues?${params.toString()}`)
      .then(({ data: d }) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, "Failed to load issues"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, typeFilter, debouncedSearch]);

  useEffect(() => load(), [load, reloadFlag]);

  function openIssue(row: IssueItem) {
    setSelected(row);
    setNextStatus("");
    setNote(row.statusNote ?? "");
  }

  async function saveStatus() {
    if (!selected || !nextStatus) return;
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/issues/${selected.id}/status`, {
        status: nextStatus,
        statusNote: note.trim() || undefined,
      });
      setSelected(null);
      setReloadFlag((n) => n + 1);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to update the issue"));
    } finally {
      setSaving(false);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  return (
    <>
      <div className="adm__body">
        <div className="adm__page-head" style={{ marginBottom: 16 }}>
          <div className="adm__title-stack">
            <div className="meta">↗ THINGS THAT NEED A HUMAN</div>
            <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
              Issues
            </h1>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, maxWidth: 460 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--surface-2)",
                border: "1px solid var(--line)",
                padding: "8px 14px",
              }}
            >
              <Input
                type="search"
                placeholder="Search by serial, brand, model or document…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  background: "transparent",
                  border: 0,
                  outline: 0,
                  color: "var(--ink)",
                  fontFamily: "var(--f-body)",
                  fontSize: 13,
                  width: "100%",
                  padding: 0,
                }}
              />
            </div>
          </div>
          <select
            className="select"
            style={{
              width: "auto",
              padding: "9px 32px 9px 14px",
              fontSize: 12,
              appearance: "none",
            }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTERS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <select
            className="select"
            style={{
              width: "auto",
              padding: "9px 32px 9px 14px",
              fontSize: 12,
              appearance: "none",
            }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {TYPE_FILTERS.map((o) => (
              <option key={o} value={o}>
                {TYPE_LABELS[o] ?? o}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setStatusFilter("All status");
              setTypeFilter("All types");
            }}
          >
            Clear
          </Button>
        </div>

        {error && (
          <div
            style={{
              padding: "12px 16px",
              border: "1px solid var(--danger)",
              color: "var(--danger)",
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Issue</TableHead>
              <TableHead>Watch</TableHead>
              <TableHead>Document</TableHead>
              <TableHead>Raised</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-mute)",
                    fontFamily: "var(--f-mono)",
                    fontSize: 11,
                    padding: "32px 0",
                  }}
                >
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && (data?.items.length ?? 0) === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  style={{
                    textAlign: "center",
                    color: "var(--ink-mute)",
                    fontFamily: "var(--f-mono)",
                    fontSize: 11,
                    padding: "32px 0",
                  }}
                >
                  Nothing to review
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              data?.items.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => openIssue(row)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>
                    {TYPE_LABELS[row.type] ?? row.type}
                    <span
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: "var(--ink-mute)",
                      }}
                    >
                      {row.title}
                    </span>
                  </TableCell>
                  <TableCell>
                    {[row.brand, row.model].filter(Boolean).join(" · ") || "—"}
                    <span
                      className="mono"
                      style={{
                        display: "block",
                        fontSize: 11,
                        color: "var(--ink-mute)",
                      }}
                    >
                      {row.serialNumber ?? ""}
                    </span>
                  </TableCell>
                  <TableCell className="mono">
                    {row.sourceNumber || row.sourceType || "—"}
                  </TableCell>
                  <TableCell className="mono">
                    {fmtDate(row.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Pill
                      label={row.severity}
                      color={severityColors[row.severity]}
                    />
                  </TableCell>
                  <TableCell>
                    <Pill label={row.status} color={statusColors[row.status]} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {data && data.total > LIMIT && (
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <span
              className="mono-cap text-mute"
              style={{ fontSize: 11, letterSpacing: "0.12em" }}
            >
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal
        open={!!selected}
        title="Issue detail"
        onClose={() => setSelected(null)}
      >
        {selected && (
          <div style={{ display: "grid", gap: 18 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <Pill
                label={selected.severity}
                color={severityColors[selected.severity]}
              />
              <Pill
                label={selected.status}
                color={statusColors[selected.status]}
              />
            </div>

            <Field
              label="What happened"
              value={TYPE_LABELS[selected.type] ?? selected.type}
            />
            <Field label="Detail" value={selected.title} />

            {selected.brand && selected.model && selected.serialNumber && (
              <div style={{ display: "grid", gap: 4 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Watch
                </span>
                <Link
                  to="/dpp"
                  search={{
                    brand: selected.brand,
                    model: selected.model,
                    serial: selected.serialNumber,
                  }}
                  style={{ fontSize: 14, color: "var(--accent)" }}
                >
                  {selected.brand} · {selected.model} · {selected.serialNumber}
                </Link>
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              <Field
                label="Source"
                value={
                  [selected.sourceType, selected.sourceNumber]
                    .filter(Boolean)
                    .join(" ") || "—"
                }
              />
              <Field label="Raised" value={fmtDate(selected.createdAt)} />
            </div>

            {selected.detail && Object.keys(selected.detail).length > 0 && (
              <div style={{ display: "grid", gap: 4 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Context
                </span>
                <pre
                  style={{
                    fontFamily: "var(--f-mono)",
                    fontSize: 11,
                    color: "var(--ink-soft)",
                    background: "var(--surface-2)",
                    border: "1px solid var(--line)",
                    padding: 12,
                    margin: 0,
                    overflowX: "auto",
                  }}
                >
                  {JSON.stringify(selected.detail, null, 2)}
                </pre>
              </div>
            )}

            {canManage && NEXT_STATUSES[selected.status].length > 0 && (
              <div style={{ display: "grid", gap: 10 }}>
                <span
                  className="mono-cap text-mute"
                  style={{ fontSize: 10, letterSpacing: "0.12em" }}
                >
                  Move to
                </span>
                <select
                  className="select"
                  value={nextStatus}
                  onChange={(e) =>
                    setNextStatus(e.target.value as IssueStatus | "")
                  }
                  style={{
                    padding: "10px 14px",
                    fontSize: 13,
                    appearance: "none",
                  }}
                >
                  <option value="">Choose a status…</option>
                  {NEXT_STATUSES[selected.status].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <Button
                  onClick={saveStatus}
                  disabled={saving || !nextStatus}
                  size="sm"
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            )}

            {selected.statusNote && (
              <Field label="Note" value={selected.statusNote} />
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
